import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Newspaper, MessageSquare, Camera, Bot, MapPin, GraduationCap, Search, ArrowRight,
  Users, Radio, Users2, CalendarDays, Clock,
} from "lucide-react";
import api from "@/lib/api";
import { useSettings } from "@/lib/settings";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { NewsCard, SectionHeading } from "@/components/public/Shared";
import { formatDate } from "@/lib/helpers";

const SERVICES = [
  { icon: Newspaper, title: "Info Bawaslu", to: "/info", desc: "Berita, kegiatan, pengumuman, dan informasi pengawasan.", color: "text-blue-600 bg-blue-50" },
  { icon: MessageSquare, title: "Suara Masyarakat", to: "/suara-masyarakat", desc: "Sampaikan saran, kritik, aspirasi, dan masukan.", color: "text-brandred bg-red-50" },
  { icon: Camera, title: "Media Center", to: "/media-center", desc: "Press release, dokumentasi, dan kebutuhan media.", color: "text-purple-600 bg-purple-50" },
  { icon: Bot, title: "Tanya Bawaslu", to: "/tanya-bawaslu", desc: "Dapatkan jawaban atas pertanyaan umum tentang Bawaslu.", color: "text-emerald-600 bg-emerald-50" },
  { icon: MapPin, title: "Bawaslu Terdekat", to: "/bawaslu-terdekat", desc: "Temukan informasi kantor Bawaslu di wilayah Anda.", color: "text-amber-600 bg-amber-50" },
  { icon: GraduationCap, title: "Edukasi Pemilu", to: "/edukasi", desc: "Pelajari pemilu dan pengawasan partisipatif.", color: "text-indigo-600 bg-indigo-50" },
];

export default function Home() {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [news, setNews] = useState([]);
  const [agendas, setAgendas] = useState([]);
  const settings = useSettings();
  const hero = {
    badge: settings?.hero_badge || "Portal Resmi Bawaslu Provinsi Sulawesi Tenggara",
    title: settings?.hero_title || "HARMONI",
    highlight: settings?.hero_highlight || "BAWASLU",
    tagline: settings?.hero_tagline || '"Satu Pintu Komunikasi Publik Bawaslu"',
    description: settings?.hero_description || "Platform komunikasi publik yang menghubungkan Bawaslu dengan masyarakat, media, komunitas, dan pemangku kepentingan melalui layanan informasi yang cepat, terbuka, responsif, dan terintegrasi.",
  };

  useEffect(() => {
    api.get("/news?limit=6").then((r) => setNews(r.data.items)).catch(() => {});
    api.get("/agendas").then((r) => setAgendas(r.data.slice(0, 5))).catch(() => {});
  }, []);

  const search = (e) => {
    e.preventDefault();
    navigate(`/info?search=${encodeURIComponent(q)}`);
  };

  return (
    <div>
      {/* HERO */}
      <section className="relative bg-navy text-white overflow-hidden">
        <div className="absolute inset-0 opacity-20" style={{ backgroundImage: "radial-gradient(circle at 15% 20%, #DC2626 0, transparent 35%), radial-gradient(circle at 85% 80%, #1E3E6B 0, transparent 40%)" }} />
        <div className="container-page py-16 sm:py-24 relative grid lg:grid-cols-2 gap-12 items-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <div className="inline-flex items-center gap-2 bg-white/10 border border-white/15 rounded-full px-4 py-1.5 text-xs font-semibold mb-6">
              <span className="h-2 w-2 rounded-full bg-brandred animate-pulse" />
              {hero.badge}
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-[1.05]">
              {hero.title} <span className="text-brandred">{hero.highlight}</span>
            </h1>
            <p className="text-xl sm:text-2xl font-semibold text-slate-200 mt-4">
              {hero.tagline}
            </p>
            <p className="text-slate-300 mt-4 max-w-xl leading-relaxed">
              {hero.description}
            </p>
            <form onSubmit={search} className="mt-8 flex gap-2 bg-white rounded-full p-1.5 shadow-2xl max-w-lg" data-testid="hero-search-form">
              <div className="flex items-center flex-1 pl-4">
                <Search className="h-5 w-5 text-slate-400" />
                <Input
                  data-testid="hero-search-input"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder={settings?.search_placeholder || "Cari informasi Bawaslu..."}
                  className="border-0 shadow-none focus-visible:ring-0 text-slate-800 text-base"
                />
              </div>
              <Button type="submit" className="bg-brandred hover:bg-brandred-700 text-white rounded-full px-7 font-semibold">
                Cari
              </Button>
            </form>
          </motion.div>

          {/* SATU PINTU DIAGRAM */}
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6, delay: 0.2 }}
            className="bg-white/5 border border-white/10 rounded-2xl p-6 sm:p-8 backdrop-blur" data-testid="satu-pintu-diagram">
            <div className="text-center">
              <div className="inline-flex flex-col items-center">
                <div className="px-5 py-2 rounded-lg bg-white/10 font-bold text-sm tracking-wide">BAWASLU</div>
                <div className="h-6 w-px bg-white/30" />
                <div className="px-6 py-2.5 rounded-lg bg-brandred font-extrabold shadow-lg">HARMONI BAWASLU</div>
                <div className="h-6 w-px bg-white/30" />
              </div>
              <div className="grid grid-cols-3 gap-3 mt-2">
                {[
                  { icon: Users, t: "Masyarakat", s: "Informasi & Aspirasi" },
                  { icon: Radio, t: "Media", s: "Media Center" },
                  { icon: Users2, t: "Komunitas", s: "Edukasi & Partisipasi" },
                ].map((c) => (
                  <div key={c.t} className="bg-white/10 rounded-xl p-4 flex flex-col items-center gap-2">
                    <c.icon className="h-6 w-6 text-brandred" />
                    <div className="text-sm font-bold">{c.t}</div>
                    <div className="text-[10px] text-slate-300 leading-tight">{c.s}</div>
                  </div>
                ))}
              </div>
              <p className="text-xs text-slate-400 mt-5">{settings?.diagram_caption || 'Konsep "Satu Pintu" — seluruh komunikasi publik terintegrasi dalam satu kanal.'}</p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* SERVICE CARDS */}
      <section className="container-page -mt-10 relative z-10">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {SERVICES.map((s, i) => (
            <motion.div key={s.title} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.05 }}>
              <Link to={s.to} data-testid={`service-card-${i + 1}`}
                className="group block bg-white rounded-xl border border-slate-200 p-6 hover:shadow-xl hover:border-brandred/30 transition-all h-full">
                <div className={`h-14 w-14 rounded-xl flex items-center justify-center ${s.color} mb-4`}>
                  <s.icon className="h-7 w-7" />
                </div>
                <h3 className="font-bold text-navy text-lg mb-2 group-hover:text-brandred transition-colors">{s.title}</h3>
                <p className="text-sm text-slate-500 leading-relaxed mb-4">{s.desc}</p>
                <span className="inline-flex items-center gap-1.5 text-sm font-bold text-brandred">
                  Selengkapnya <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                </span>
              </Link>
            </motion.div>
          ))}
        </div>
      </section>

      {/* BERITA TERKINI */}
      <section className="container-page mt-20">
        <SectionHeading
          eyebrow="Informasi Terbaru"
          title="Berita Terkini"
          description="Informasi terbaru seputar pengawasan dan kegiatan Bawaslu Provinsi Sulawesi Tenggara."
          action={<Button onClick={() => navigate("/info")} variant="outline" className="border-navy text-navy hover:bg-navy hover:text-white font-semibold rounded-full" data-testid="lihat-semua-berita">Lihat Semua Berita</Button>}
        />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {news.map((n, i) => <NewsCard key={n.id} item={n} testid={`home-news-${i}`} />)}
        </div>
      </section>

      {/* AGENDA */}
      <section className="container-page mt-20">
        <SectionHeading eyebrow="Jadwal Kegiatan" title="Agenda Bawaslu"
          action={<Button onClick={() => navigate("/agenda")} variant="outline" className="border-navy text-navy hover:bg-navy hover:text-white font-semibold rounded-full">Semua Agenda</Button>} />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {agendas.map((a) => (
            <div key={a.id} className="bg-white rounded-xl border border-slate-200 p-5 flex gap-4 hover:shadow-lg transition-shadow" data-testid={`agenda-card-${a.id}`}>
              <div className="shrink-0 h-16 w-16 rounded-xl bg-navy text-white flex flex-col items-center justify-center">
                <span className="text-xl font-extrabold leading-none">{new Date(a.date).getDate()}</span>
                <span className="text-[10px] uppercase">{new Date(a.date).toLocaleDateString("id-ID", { month: "short" })}</span>
              </div>
              <div className="min-w-0">
                <h4 className="font-bold text-navy leading-snug line-clamp-2">{a.title}</h4>
                <div className="text-xs text-slate-500 mt-2 space-y-1">
                  <div className="flex items-center gap-1.5"><Clock className="h-3.5 w-3.5" /> {a.time}</div>
                  <div className="flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5" /> {a.location}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="container-page mt-20">
        <div className="bg-navy rounded-2xl overflow-hidden relative">
          <div className="absolute inset-0 opacity-15" style={{ backgroundImage: "radial-gradient(circle at 80% 30%, #DC2626 0, transparent 40%)" }} />
          <div className="relative p-8 sm:p-12 flex flex-col sm:flex-row items-center justify-between gap-6 text-white">
            <div>
              <h3 className="text-2xl sm:text-3xl font-extrabold">{settings?.cta_title || "Punya aspirasi untuk Bawaslu?"}</h3>
              <p className="text-slate-300 mt-2">{settings?.cta_description || "Sampaikan saran, kritik, dan masukan Anda melalui Suara Masyarakat."}</p>
            </div>
            <Button onClick={() => navigate("/suara-masyarakat")} className="bg-brandred hover:bg-brandred-700 text-white rounded-full px-8 h-12 font-semibold text-base shrink-0">
              {settings?.cta_button || "Sampaikan Aspirasi"}
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}
